import type { KhoangBinhThuongDto, NguonSoLieu, TheChiSo as TheChiSoDto } from '@qlk/shared';

import { gonSo, phanTram, thoiLuong } from '../dinhDang.js';

/**
 * Hàng thẻ chỉ số + biểu đồ, dùng chung cho cả bốn tab.
 *
 * Thẻ đang chọn nền trắng, thẻ khác nền xám; bấm thẻ đổi biểu đồ bên dưới. Dòng
 * phụ in nghiêng nói **so với thường lệ** (khoảng app tự tính từ lịch sử kênh)
 * nếu có, không thì so với kỳ trước.
 */

/** Chấm màu theo nguồn số liệu, kèm giải thích khi rê chuột. */
export function ChamNguon({ nguon }: { nguon: NguonSoLieu }) {
  const mau =
    nguon === 'analytics' ? 'bg-ok-chu' : nguon === 'reporting' ? 'bg-warn-chu' : 'bg-neutral-chu';

  const giaiThich =
    nguon === 'analytics'
      ? 'YouTube Analytics API — khớp Studio'
      : nguon === 'reporting'
        ? 'YouTube Reporting API — trễ khoảng 48 giờ'
        : 'App tự tính từ lịch sử của kênh';

  return (
    <span
      aria-hidden="true"
      title={giaiThich}
      className={`inline-block size-1.5 rounded-full ${mau}`}
    />
  );
}

export function dinhDangGiaTri(giaTri: number | null, dinhDang: TheChiSoDto['dinhDang']): string {
  /* `null` = không có dữ liệu. Không bao giờ hiện 0 thay cho "không biết". */
  if (giaTri === null) return '—';

  switch (dinhDang) {
    case 'gio':
      // Giá trị lưu theo phút, hiển thị theo giờ như Studio.
      return gonSo(Math.round(giaTri / 60));
    case 'tien':
      return `${giaTri.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} US$`;
    case 'phan-tram':
      return phanTram(giaTri);
    case 'thoi-luong':
      return thoiLuong(giaTri);
    default:
      return gonSo(giaTri);
  }
}

/**
 * Dòng thứ ba của ô kiểu `o`: `▲ 3,7% so kỳ trước`.
 *
 * **Không** in nghiêng và **không** nhắc lại khoảng ngày: khoảng của kỳ trước
 * đã ghi một lần ở dòng phụ dưới tiêu đề trang, in lại trong tám ô là tám lần
 * cùng một chuỗi — và chuỗi đó dài hơn chính con số.
 *
 * Ghi chú (ví dụ "chỉ có từ khi app tạo reporting job") đứng **trước** mọi so
 * sánh: so một con số thiếu ngày với kỳ trước chỉ gây hiểu sai.
 */
function DongDoiCuaO({ ghiChu, delta }: { ghiChu: string | null; delta: number | null }) {
  if (ghiChu !== null) return <span className="text-bang text-chu-mo">{ghiChu}</span>;
  if (delta === null) {
    return <span className="text-bang text-chu-mo">Chưa so được với kỳ trước</span>;
  }

  const tang = delta >= 0;
  return (
    <span
      data-huong={tang ? 'tang' : 'giam'}
      className={`so-lieu text-bang font-semibold ${tang ? 'text-tang' : 'text-giam'}`}
    >
      {tang ? '▲' : '▼'} {phanTram(Math.abs(delta))} so kỳ trước
    </span>
  );
}

/** Câu "Cao hơn 84,6 N so với bình thường" / "Như bình thường". */
export function dongPhuBinhThuong(
  bt: KhoangBinhThuongDto,
  dinhDang: TheChiSoDto['dinhDang'],
): string {
  if (bt.ketQua === 'binh-thuong') return 'Như bình thường';

  const lech = bt.ketQua === 'cao' ? bt.giaTri - bt.cao : bt.thap - bt.giaTri;
  const huong = bt.ketQua === 'cao' ? 'Cao hơn' : 'Thấp hơn';
  return `${huong} ${dinhDangGiaTri(lech, dinhDang)} so với bình thường`;
}

function BieuTuong({ bt, delta }: { bt: KhoangBinhThuongDto | null; delta: number | null }) {
  if (bt) {
    if (bt.ketQua === 'cao') {
      return (
        <span
          aria-label="Cao hơn bình thường"
          className="inline-flex size-5 items-center justify-center rounded-full bg-ok-nen text-phu text-ok-chu"
        >
          ⬆
        </span>
      );
    }
    if (bt.ketQua === 'thap') {
      return (
        <span
          aria-label="Thấp hơn bình thường"
          className="inline-flex size-5 items-center justify-center rounded-full bg-neutral-nen text-phu text-chu-phu"
        >
          ⬇
        </span>
      );
    }
    return (
      <span aria-label="Như bình thường" className="text-phu text-ok-chu">
        ✓
      </span>
    );
  }

  if (delta === null) return null;
  return (
    <span
      aria-label={delta >= 0 ? 'Tăng so với kỳ trước' : 'Giảm so với kỳ trước'}
      className={`text-phu ${delta >= 0 ? 'text-ok-chu' : 'text-chu-phu'}`}
    >
      {delta >= 0 ? '⬆' : '⬇'}
    </span>
  );
}

/**
 * Số cột của hàng thẻ theo **bề ngang của cột chứa nó**, không theo khung nhìn.
 *
 * Giá trị dài nhất là một khoản tiền: "1.809,00 US$" chiếm khoảng 190px ở cỡ
 * 26px, cộng padding 32px là **~235px** mỗi thẻ. Bản cũ xếp `repeat(N, 1fr)`
 * cố định nên bốn thẻ cũng chen vào một hàng dù cột chỉ rộng 730px — và số
 * tiền gãy làm hai dòng.
 *
 * Lớp phải tĩnh để Tailwind quét thấy.
 */
const COT_HANG_THE: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 @min-[705px]:grid-cols-3',
  4: 'grid-cols-2 @min-[940px]:grid-cols-4',
  5: 'grid-cols-2 @min-[1175px]:grid-cols-5',
  6: 'grid-cols-2 @min-[705px]:grid-cols-3 @min-[1410px]:grid-cols-6',
  /*
   * Bảy và tám ô (trang Hiệu suất) **không** cố vào một hàng: tám thẻ ×235px
   * là 1880px, rộng hơn mọi màn hình công ty đang dùng. Hai hàng bốn ô là bố
   * cục thật của nó, và 390px vẫn hai cột như mọi hàng thẻ khác.
   */
  7: 'grid-cols-2 @min-[705px]:grid-cols-4',
  8: 'grid-cols-2 @min-[705px]:grid-cols-4',
};

/**
 * Cỡ của con số **theo bề ngang của chính thẻ**.
 *
 * Hai cột trên điện thoại cho mỗi thẻ 179px, trừ padding còn 147px — cỡ 26px
 * cần 190px nên số gãy đôi, còn 18px chỉ cần ~133px. Thu nhỏ một nấc rẻ hơn
 * nhiều so với bắt cả hàng xuống một cột.
 */
const CO_SO_THE = 'text-tieu-de-hop @min-[185px]:text-so-lon @min-[240px]:text-so-rat-lon';

/* Bảy và tám ô luôn là **hai hàng bốn ô** từ 705px: tám ô một hàng cần 1880px,
   rộng hơn mọi màn hình công ty đang dùng (xem `docs/ui.md` mục 9). */

export interface HangTheProps {
  the: TheChiSoDto[];
  dangChon: string;
  onChon: (khoa: string) => void;
  /**
   * Khoảng của kỳ trước (`01/09 – 26/09/2026`) để dòng so sánh nói rõ mình so
   * với ngày nào. Bỏ trống thì vẫn là "so với kỳ trước" như trước.
   *
   * ⚠️ Không dùng ở `kieu="o"`: trang Hiệu suất đã ghi khoảng kỳ trước ở **dòng
   * phụ dưới tiêu đề trang**, in lại trong tám ô là tám lần cùng một chuỗi.
   */
  nhanKyTruoc?: string;
  /**
   * Hình dáng của ô.
   *
   * - `the` (mặc định): bốn tab trang kênh — căn giữa, có chấm nguồn số liệu,
   *   có biểu tượng cạnh số, dòng phụ nói "so với thường lệ" khi có khoảng
   *   thường lệ.
   * - `o`: trang Hiệu suất — **căn trái** ba dòng (tên · số · mức đổi), không
   *   chấm nguồn, không biểu tượng cạnh số. Tám ô mà mỗi ô có chấm màu, đồng
   *   hồ và một mũi tên nhỏ thì phần trang trí nhiều hơn số liệu.
   */
  kieu?: 'the' | 'o';
}

export function HangThe({ the, dangChon, onChon, nhanKyTruoc, kieu = 'the' }: HangTheProps) {
  const soVoi = nhanKyTruoc === undefined || nhanKyTruoc === '' ? 'kỳ trước' : nhanKyTruoc;
  const o = kieu === 'o';

  return (
    <div
      role="group"
      aria-label="Chỉ số"
      /* Vạch giữa các ô là `gap-px` trên nền viền: một đường 1px thật, không
         phải hai viền chồng nhau. Kiểu `o` dùng tông nhạt hơn
         (`--color-vien-trong`) vì tám ô mà kẻ đậm thì lưới nổi hơn số. */
      className={`grid gap-px overflow-hidden rounded-t-the border border-vien ${
        o ? 'bg-vien-trong' : 'bg-vien'
      } ${COT_HANG_THE[the.length] ?? 'grid-cols-2'}`}
    >
      {the.map((t) => {
        const chon = t.khoa === dangChon;

        return (
          <button
            key={t.khoa}
            type="button"
            /*
             * `aria-pressed`, không `role="tab"`/`aria-selected`: hàng thẻ là
             * một nhóm **nút bật-tắt chọn-một**, và trên trang Hiệu suất nó đổi
             * cả biểu đồ lẫn khung so sánh lẫn URL — không phải một tab panel.
             */
            aria-pressed={chon}
            onClick={() => {
              onChon(t.khoa);
            }}
            /* `@container` trên **chính thẻ**: cỡ số đo theo chỗ của thẻ, không
               theo chỗ của cả hàng.

               Thẻ đang chọn: nền bề mặt + **gạch dưới màu nhấn**. Chỉ đổi nền
               thì ở hai hàng tám ô rất khó thấy ô nào đang chọn; viền dưới 2px
               nằm trong `box-border` nên không làm thẻ cao thêm. */
            className={
              o
                ? `vung-bam @container flex flex-col items-start gap-1 border-b-[3px] px-[18px] py-4 text-left ${
                    chon
                      ? 'border-nhan bg-nhan-nen'
                      : 'border-transparent bg-be-mat hover:bg-neutral-nen'
                  }`
                : `vung-bam @container flex flex-col items-center gap-1 border-b-2 px-4 py-4 text-center ${
                    chon
                      ? 'border-nhan bg-be-mat'
                      : 'border-transparent bg-neutral-nen hover:bg-vien'
                  }`
            }
          >
            <span
              className={`flex items-center gap-1.5 text-chu-phu ${o ? 'text-bang' : 'text-phu'}`}
            >
              {/* Chấm nguồn số liệu chỉ có ở bốn tab trang kênh: ở đó mỗi tab
                  trộn số của Analytics API, Reporting API và số app tự tính nên
                  phải nói ra. Tám ô của trang Hiệu suất thì nguồn đã nằm trong
                  ghi chú của từng ô khi cần. */}
              {!o && <ChamNguon nguon={t.nguon} />}
              {t.nhan}
              {t.laUocTinh && (
                <span aria-label="Số ước tính" title="YouTube ghi đây là số ước tính">
                  ⏱
                </span>
              )}
            </span>

            <span className="flex items-center gap-2">
              {/* Kiểu `the` cố ý **không** `whitespace-nowrap`: hàng thẻ có
                  `overflow-hidden`, nên số không vừa mà không được xuống dòng
                  thì bị cắt mất một cách im lặng và bài `92` không bắt được.
                  Kiểu `o` thì thiết kế đòi số **một dòng**, mà cỡ số đã tự thu
                  theo bề ngang ô nên chỗ luôn đủ. */}
              <span
                className={`so-lieu font-bold text-chu ${CO_SO_THE} ${o ? 'whitespace-nowrap' : ''}`}
              >
                {dinhDangGiaTri(t.giaTri, t.dinhDang)}
              </span>
              {/* Mũi tên nhỏ cạnh số chỉ có ở kiểu `the`: ở kiểu `o` dòng thứ ba
                  đã mở đầu bằng ▲/▼ cùng màu, hai dấu cho một ý là một dấu
                  thừa. */}
              {!o && <BieuTuong bt={t.binhThuong} delta={t.deltaPercent} />}
            </span>

            {o ? (
              <DongDoiCuaO ghiChu={t.ghiChu} delta={t.deltaPercent} />
            ) : (
              <span className="text-phu italic text-chu-mo">
                {/* Ghi chú (ví dụ khoảng Reporting đã phủ) đứng trước mọi so sánh: so một
                    con số thiếu ngày với kỳ trước chỉ gây hiểu sai. */}
                {t.ghiChu ??
                  (t.binhThuong
                    ? dongPhuBinhThuong(t.binhThuong, t.dinhDang)
                    : t.deltaPercent === null
                      ? `Chưa so được với ${soVoi}`
                      : `${t.deltaPercent >= 0 ? 'Tăng' : 'Giảm'} ${phanTram(Math.abs(t.deltaPercent))} so với ${soVoi}`)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
